import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { widgetManager } from '../utils/widgetManager';
import { buildParamKey } from '../utils/widgetKeys';
import TimeSeriesChart from './TimeSeriesChart';
import './TimeSeriesWidget.css';

function deviceLabel(deviceId, devicesById) {
  return devicesById[deviceId]?.vehicleId || deviceId;
}

function TimeSeriesWidget({ widget, token, devices, onEdit, onDelete, onDuplicate }) {
  const [viewMode, setViewMode] = useState('chart');
  const [pendingPdfExport, setPendingPdfExport] = useState(false);
  const chartRef = useRef(null);

  const filterMode = widget.config.filterMode || 'off';

  const handleFilterChange = (e) => {
    widgetManager.updateWidget(widget.id, {
      config: { ...widget.config, filterMode: e.target.value },
    });
  };

  useEffect(() => {
    if (!widget.data && !widget.isLoading && !widget.error) {
      widgetManager.loadWidgetData(widget.id, token);
    }
  }, [widget, token]);

  const handleRefresh = () => widgetManager.loadWidgetData(widget.id, token);

  const exportChartToPdf = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const imgData = chart.toBase64Image('image/png', 1.0);
    const pdf = new jsPDF({
      orientation: chart.width >= chart.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [chart.width, chart.height]
    });
    pdf.addImage(imgData, 'PNG', 0, 0, chart.width, chart.height);
    pdf.save(`${widget.title.replace(/\s+/g, '_')}_chart.pdf`);
  };

  // The chart canvas only exists in 'chart' view mode, so switch to it first
  // (if needed) and wait for the render to paint before capturing the image.
  const handleExportPdf = () => {
    if (viewMode !== 'chart') {
      setPendingPdfExport(true);
      setViewMode('chart');
      return;
    }
    exportChartToPdf();
  };

  useEffect(() => {
    if (pendingPdfExport && viewMode === 'chart') {
      setPendingPdfExport(false);
      requestAnimationFrame(() => requestAnimationFrame(exportChartToPdf));
    }
  }, [pendingPdfExport, viewMode]);

  const handleExportExcel = () => {
    if (!widget.data || widget.data.length === 0) return;
    const chartParams = buildChartParams();
    const rows = widget.data.map(row => {
      const record = { Time: new Date(row.timestamp).toLocaleString() };
      chartParams.forEach(cp => {
        record[cp.name] = row[cp.key]?.value !== undefined
          ? `${row[cp.key].value} ${row[cp.key].unit}`
          : '';
      });
      return record;
    });
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Widget Data');
    XLSX.writeFile(workbook, `${widget.title.replace(/\s+/g, '_')}_data.xlsx`);
  };

  // Merge extra devices (looked up during widget creation) with the prop list
  // so labels resolve correctly even for devices outside the current group.
  const allDevices = [
    ...(devices || []),
    ...((widget.config.extraDevices || []).filter(
      ed => !(devices || []).some(d => d.deviceId === ed.deviceId)
    ))
  ];

  const devicesById = allDevices.reduce((acc, d) => {
    acc[d.deviceId] = d;
    return acc;
  }, {});

  const buildChartParams = () => {
    const multiDevice = widget.config.selectedDevices.length > 1;
    const chartParams = [];
    widget.config.selectedBatteries.forEach(bat => {
      widget.config.selectedParameters.forEach(param => {
        const label = multiDevice
          ? `${param.name} — ${deviceLabel(bat.deviceId, devicesById)} (Slave ${bat.slaveId})`
          : `${param.name} (Slave ${bat.slaveId})`;
        chartParams.push({
          key: buildParamKey(bat.deviceId, bat.slaveId, param.key),
          name: label
        });
      });
    });
    return chartParams;
  };

  const renderContent = () => {
    if (widget.isLoading) return <div className="widget-loader">Loading telemetry data...</div>;
    if (widget.error) {
      return (
        <div className="widget-error">
          Error: {widget.error} <button onClick={handleRefresh}>Retry</button>
        </div>
      );
    }
    if (!widget.data || widget.data.length === 0) return <div className="widget-no-data">No data found in range.</div>;

    const chartParams = buildChartParams();
    const partialWarning = widget.partialErrors && widget.partialErrors.length > 0 && (
      <div className="widget-warning">
        Some devices failed to load: {widget.partialErrors.join('; ')}
        <button onClick={handleRefresh}>Retry</button>
      </div>
    );

    if (viewMode === 'chart') {
      return (
        <>
          {partialWarning}
          <TimeSeriesChart ref={chartRef} data={widget.data} parameters={chartParams} title={widget.title} filterMode={filterMode} />
        </>
      );
    }

    return (
      <>
        {partialWarning}
        <div className="table-container widget-table">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                {chartParams.map(cp => <th key={cp.key}>{cp.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {widget.data.slice(0, 100).map((row, idx) => (
                <tr key={idx}>
                  <td>{new Date(row.timestamp).toLocaleString()}</td>
                  {chartParams.map(cp => (
                    <td key={cp.key}>
                      {row[cp.key]?.value !== undefined ? `${row[cp.key].value} ${row[cp.key].unit}` : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const hasData = !!(widget.data && widget.data.length > 0);

  return (
    <div className="widget-card">
      <div className="widget-card-header">
        <h4>{widget.title}</h4>
        <div className="widget-actions">
          <select
            className="widget-filter-select"
            aria-label="Outlier filtering"
            title="Outlier filtering"
            value={filterMode}
            onChange={handleFilterChange}
          >
            <option value="off">Filter: Off</option>
            <option value="light">Filter: Light</option>
            <option value="aggressive">Filter: Aggressive</option>
          </select>
          <button className="icon-btn" onClick={() => setViewMode(viewMode === 'chart' ? 'table' : 'chart')}>
            {viewMode === 'chart' ? 'Table' : 'Chart'}
          </button>
          <button className="icon-btn" onClick={handleExportExcel} disabled={!hasData} title="Export data to Excel">
            Export Excel
          </button>
          <button className="icon-btn" onClick={handleExportPdf} disabled={!hasData} title="Export chart image to PDF">
            Export PDF
          </button>
          <button className="icon-btn" onClick={handleRefresh}>Refresh</button>
          <button className="icon-btn" onClick={() => onEdit(widget)}>Edit</button>
          <button className="icon-btn" onClick={() => onDuplicate(widget.id)}>Duplicate</button>
          <button className="icon-btn delete" onClick={() => onDelete(widget.id)}>Delete</button>
        </div>
      </div>
      <div className="widget-card-body">{renderContent()}</div>
    </div>
  );
}

export default TimeSeriesWidget;
